import { Section, SectionHeader } from "@/components/ui/Section";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Button } from "@/components/ui/button";

const testimonials = [
  {
    quote: "FounderMitra helped us think beyond just a website. From building our academy platform to shaping the broader ecommerce and growth ecosystem, their guidance has been strategic, practical, and founder-first. We're building with far more clarity and confidence.",
    company: "RareFind Arts Academy",
    role: "Founder / Core Team",
    tags: ["Arts", "Education", "Digital Commerce"],
  },
  {
    quote: "FounderMitra played a key role in shaping our platform direction. From positioning to feature prioritization and go-to-market clarity, their inputs helped us avoid common early-stage mistakes and move faster with conviction.",
    company: "SaaSBaazar",
    role: "Founding Team",
    tags: ["SaaS", "ERP", "B2B Platforms"],
  },
  {
    quote: "What stood out was the thinking. FounderMitra helped us stress-test our product, tech choices, and roadmap before we committed serious capital. That alone saved us months of wrong execution.",
    company: "Confidential Founder",
    role: "B2B SaaS Founder",
    tags: ["SaaS", "Early Stage"],
  },
  {
    quote: "We didn't need more execution bandwidth. We needed clarity. FounderMitra helped us simplify decisions across product, tech, and priorities when things were getting noisy.",
    company: "Growth-Stage Startup Team",
    role: "Consumer Tech",
    tags: ["Consumer Tech", "Growth Stage"],
  },
];

export function TestimonialsSection() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true, 
    align: "start",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Auto-play
  useEffect(() => {
    if (!emblaApi) return;
    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [emblaApi]);

  return (
    <Section background="dark">
      <SectionHeader 
        eyebrow="Voices of founders"
        variant="dark"
        title="What founders say after working with us."
        subtitle="Strategic clarity, faster decisions, fewer wrong turns. In their own words."
      />

      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-5">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_50%] pl-5"
              >
                <motion.div
                  className="h-full"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className="rounded-2xl p-7 md:p-8 border border-white/10 bg-white/[0.04] backdrop-blur-md h-full flex flex-col transition-all duration-500"
                    whileHover={{ 
                      y: -6, 
                      boxShadow: "0 30px 60px -25px rgba(0,0,0,0.6)",
                      borderColor: "rgba(255,255,255,0.25)",
                    }}
                  >
                    <div className="text-5xl font-serif text-accent/70 leading-none mb-4">"</div>
                    <blockquote className="text-base md:text-[17px] leading-[1.7] text-white/85 mb-6 flex-grow">
                      {testimonial.quote}
                    </blockquote>
                    <div className="pt-5 border-t border-white/10">
                      <p className="font-display font-semibold text-base text-white">{testimonial.company}</p>
                      <p className="text-sm text-white/55 mb-3">{testimonial.role}</p>
                      <div className="flex flex-wrap gap-2">
                        {testimonial.tags.map((tag) => (
                          <span 
                            key={tag} 
                            className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-accent font-medium uppercase tracking-wider"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-5 mt-8">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              className="rounded-full border border-white/20 bg-white/[0.06] text-white hover:text-white hover:bg-white/15 hover:border-white/30 transition-all h-10 w-10"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </motion.div>
          
          <div className="flex gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi?.scrollTo(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === selectedIndex 
                    ? "w-6 bg-accent" 
                    : "w-2 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              className="rounded-full border border-white/20 bg-white/[0.06] text-white hover:text-white hover:bg-white/15 hover:border-white/30 transition-all h-10 w-10"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
